import { cn, Pill, Stack } from 'component-library';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { printDDMMYYYY } from '@/utils/formatting/date';

export type Article = {
  id: string;
  report_name: string;
  report_date: string;
  thumbnail_url: string;
  to?: string;
};

type ArticleCardProps = {
  article: Article;
  onClick?: (article: Article) => void;
  className?: string;
};

export const ArticleCard = ({ article, onClick, className }: ArticleCardProps) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(article);
  };

  return (
    <Stack
      onClick={handleClick}
      data-testid='article-card'
      spacing={3}
      className={cn('cursor-pointer', className)} // No border class
    >
      <ArticleThumbnail article={article} />
      <ArticleInfo article={article} formatDate={(date: string) => printDDMMYYYY(date) ?? '--'} />
    </Stack>
  );
};

const ArticleThumbnail = ({ article }: { article: Article }) => {
  return (
    <div data-testid='article-card-thumbnail'>
      <img
        src={article.thumbnail_url}
        alt={`Thumbnail for ${article.report_name}`}
        className='w-full h-48 object-cover rounded-2xl border border-divider' // Full width, fixed height
      />
    </div>
  );
};

const ArticleInfo = ({ article, formatDate }: { article: Article; formatDate: (date: string) => string }) => {
  const { t } = useTranslation();
  return (
    <Stack data-testid='article-card-info' spacing={2}>
      <Pill size='small' className='bg-neutral-black-12 text-neutral-black-80 self-start'>
        {t('knowledgeBase.articlePillLabel')}
      </Pill>
      <span className='typography-h6 text-text-primary line-clamp-2'>{article.report_name}</span>
      <span className='typography-body2 text-text-secondary'>{formatDate(article.report_date)}</span>
    </Stack>
  );
};
